<script setup>
import { computed, inject, toValue } from 'vue';
import { useField, FormContextKey } from 'vee-validate';

const props = defineProps({
    id: {
        type: String,
        default: undefined
    },
    // Classes da coluna em volta do campo.
    classeColuna: {
        type: String,
        default: 'col-12'
    },
    // Nome do campo no schema do useForm. Quando informado, o componente se conecta
    // sozinho ao formulário pai (valor e erro), dispensando v-model e :erro.
    name: {
        type: String,
        default: null
    },
    erro: {
        type: String,
        default: null
    },
    modelValue: {
        type: String,
        default: ''
    },
    titulo: {
        type: String,
        default: ''
    },
    // Dica exibida ao lado do título (ex.: regra entre campos relacionados).
    help: {
        type: String,
        default: null
    },
    // Máscara do InputMask (ex.: '99/99/9999', '99/9999', '99999-999'). Uma lista alterna
    // entre as máscaras conforme a quantidade de dígitos (ex.: telefone fixo e celular).
    mascara: {
        type: [String, Array],
        required: true
    },
    // Sem placeholder, mostra a própria máscara.
    placeholder: {
        type: String,
        default: null
    },
    // Guarda o valor sem os caracteres da máscara (ex.: CPF só com os 11 dígitos). Só vale para
    // máscara única: com uma lista, o valor fica com a máscara e a view tira na hora de enviar.
    semMascara: {
        type: Boolean,
        default: false
    },
    disabled: {
        type: Boolean,
        default: false
    },
    // Spinner dentro do campo (ex.: consulta do CNPJ). Sem a prop, o campo não ganha o IconField
    // em volta; com ela, o envoltório fica fixo para o input não ser recriado (e perder o foco)
    // quando o spinner liga e desliga. Só vale para máscara única.
    carregando: {
        type: Boolean,
        default: undefined
    },
    // Normalmente dispensável: a obrigatoriedade vem do schema. Serve para forçar o
    // asterisco em regra condicional (`when`), que o schema não expõe estaticamente.
    obrigatorio: {
        type: Boolean,
        default: false
    }
});

const emit = defineEmits(['update:modelValue', 'change']);

const campoForm = props.name ? useField(() => props.name) : null;

const texto = computed({
    get: () => (campoForm ? campoForm.value.value : props.modelValue) ?? '',
    set: (value) => {
        if (campoForm) campoForm.value.value = value;
        emit('update:modelValue', value);
        emit('change', value);
    }
});

const mascaraVariavel = computed(() => Array.isArray(props.mascara));

// O v-mask (vue-the-mask) usa # para dígito, no lugar do 9 do InputMask.
const mascarasVueTheMask = computed(() => mascaraVariavel.value ? props.mascara.map((mascara) => mascara.replace(/9/g, '#')) : []);

const usaCarregamento = computed(() => props.carregando !== undefined);

const placeholder = computed(() => props.placeholder ?? (mascaraVariavel.value ? props.mascara.at(-1) : props.mascara));

const mensagemErro = computed(() => props.erro ?? campoForm?.errorMessage.value ?? null);

const formulario = inject(FormContextKey, null);

// O asterisco de obrigatório sai da própria regra do schema (ex.: regraTextoMask() vs
// regraTextoMask({ obrigatorio: false })), para não ser declarado duas vezes.
const obrigatorioDoSchema = computed(() => {
    if (!props.name) return null;

    try {
        const campo = toValue(formulario?.schema)?.describe?.()?.fields?.[props.name];
        return campo ? campo.optional === false : null;
    } catch {
        return null;
    }
});

// O schema só acrescenta o asterisco: a prop continua vencendo, porque um `when`
// aparece como opcional no describe() e ainda assim é obrigatório em tempo de uso.
const obrigatorio = computed(() => props.obrigatorio || obrigatorioDoSchema.value === true);
</script>

<template>
    <div class="field mb-2" :class="props.classeColuna">
        <SmallTitle v-if="props.titulo" :titulo="props.titulo" :obrigatorio="obrigatorio" :help="props.help" />
        <!-- O InputMask não alterna máscaras: com uma lista, usa o v-mask num input com o mesmo visual. -->
        <input v-if="mascaraVariavel" :id="props.id" v-model="texto" v-mask="mascarasVueTheMask" type="text"
            :placeholder="placeholder" :disabled="props.disabled" class="p-inputtext p-component text-sm w-full"
            :class="{ 'p-invalid': mensagemErro, 'p-filled': texto }" />
        <IconField v-else-if="usaCarregamento">
            <InputIcon v-if="props.carregando" class="pi pi-spin pi-spinner" />
            <InputMask :id="props.id" v-model="texto" :mask="props.mascara" :placeholder="placeholder" :unmask="props.semMascara"
                :disabled="props.disabled" class="text-sm" :class="{ 'p-invalid': mensagemErro }" />
        </IconField>
        <InputMask v-else :id="props.id" v-model="texto" :mask="props.mascara" :placeholder="placeholder" :unmask="props.semMascara"
            :disabled="props.disabled" class="text-sm" :class="{ 'p-invalid': mensagemErro }" />
        <SmallError v-if="mensagemErro" :error="mensagemErro" />
    </div>
</template>
